'use client';

import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { type UseEmblaCarouselType } from 'embla-carousel-react';
import { Picture } from 'app/components/Picture';
import { Video } from 'app/components/Video';
import {
    getS3ObjectUrl,
    getStaticImageSource,
    getDynamicImageSource,
} from 'app/libs/url';
import { type BuyerLayoutProps } from 'shop-ui/types';
import STYLE from './ProductImageGallery.module.scss';
import { type SlideOptions } from './ProductImageGallery';

interface ImageGalleryThumbItemPropType extends BuyerLayoutProps<Product> {
    selected: boolean;
    onClick: () => void;
    index: number;
    src: string;
}

interface ImageGalleryThumbPropType extends BuyerLayoutProps<Product> {
    thumbRef: EmblaViewportRefType;
    selectedIndex: number;
    api: {
        main: UseEmblaCarouselType[1];
        thumb: UseEmblaCarouselType[1];
    };
    slideOptions: SlideOptions['slideOptions'];
    isEditingMode: boolean;
}

type EmblaViewportRefType = UseEmblaCarouselType[0];

const ImageGalleryThumbItem: React.FC<ImageGalleryThumbItemPropType> = (props) => {
    const { selected, onClick, index, isBuyer, src } = props;

    const source = useMemo(() => {
        if (isBuyer) {
            return getDynamicImageSource(src);
        }

        return getStaticImageSource(
            getS3ObjectUrl(
                `/static/image-gallery/image-gallery-presentation-${index + 1}.png`
            )
        );
    }, [isBuyer, src, index]);

    return (
        <button
            onClick={onClick}
            type="button"
            className={clsx(STYLE.thumbsSlide, selected && STYLE.thumbsSlideSelected)}
        >
            <div className={STYLE.thumbsSlideInner}>
                {source.type.startsWith('image') && (
                    <Picture
                        sources={[{ srcSet: source.srcSet, type: source.type }]}
                        alt={`Product Image - ${index + 1}`}
                    />
                )}
                {source.type.startsWith('video') && (
                    <Video
                        src={source.src}
                        videoProps={{
                            autoPlay: false,
                            poster: (source as unknown as { poster: string })?.poster,
                        }}
                    />
                )}
            </div>
        </button>
    );
};

interface DefaultThumbProps extends BuyerLayoutProps<Product> {
    selectedIndex: number;
    onThumbClick: (index: number) => void;
}

export const DefaultThumb = (props: DefaultThumbProps) => {
    const { selectedIndex, onThumbClick, isBuyer } = props;

    return Array(10)
        .fill(null)
        .map((_, idx) => (
            <ImageGalleryThumbItem
                key={idx}
                onClick={() => onThumbClick(idx)}
                selected={idx === selectedIndex}
                index={idx}
                isBuyer={isBuyer}
                src={''}
            />
        ));
};

export const ImageGalleryThumb: React.FC<ImageGalleryThumbPropType> = (props) => {
    const { selectedIndex, api, thumbRef, slideOptions, isBuyer } = props;

    const onThumbClick = useCallback(
        (index: number) => {
            if (!api.main || !api.thumb) return;
            api.main.scrollTo(index);
        },
        [api]
    );

    return (
        <div className={STYLE.thumbsBox}>
            <div className={STYLE.thumbs}>
                <div className={STYLE.thumbsViewport} ref={thumbRef}>
                    <div className={STYLE.thumbsContainer}>
                        {isBuyer &&
                            slideOptions.medias.map((media, idx) => (
                                <ImageGalleryThumbItem
                                    key={idx}
                                    onClick={() => onThumbClick(idx)}
                                    selected={idx === selectedIndex}
                                    index={idx}
                                    isBuyer={isBuyer}
                                    src={media.src}
                                />
                            ))}
                        {!isBuyer && (
                            <DefaultThumb
                                selectedIndex={selectedIndex}
                                onThumbClick={onThumbClick}
                                isBuyer={isBuyer}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
